import { Button } from "components/DesignSystem/Button/Button";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { ModalProps } from "types/utils/modal";
import { useState } from "react";
import aiExtractingImage from "static/images/AiLoad.gif";

type UpdateOption = "replace_all" | "skip_replacing";

type BillUpdateOptionsModalProps = ModalProps & {
  onReplaceBill: () => Promise<void>;
  onUploadBill: () => Promise<void>;
  isLoading?: boolean;
};

export const BillUpdateOptionsModal = ({
  isOpen,
  close,
  onReplaceBill,
  onUploadBill,
  isLoading = false,
}: BillUpdateOptionsModalProps) => {
  const [showAiExtracting, setShowAiExtracting] = useState(false);
  const handleSubmit = async (values: { updateOption: UpdateOption }) => {
    if (values.updateOption === "replace_all") {
      setShowAiExtracting(true);
      await onReplaceBill();
      setShowAiExtracting(false);
    } else {
      onUploadBill();
    }
    close();
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content asChild>
        {showAiExtracting ? (
          <div>
            <Modal.Header>
              <Modal.Title>Upload Bill</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="t-flex t-items-center t-justify-center t-gap-1.5 t-py-8">
                <img
                  src={aiExtractingImage}
                  alt="AI Extracting"
                  className="t-w-6 t-h-6"
                />
                <p className="t-m-0 t-text-subtext t-bg-ai-gradient t-bg-clip-text t-fill-surface-transparent t-text-surface-transparent">
                  AI Agent is extracting details
                </p>
              </div>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button type="button">Cancel</Button>
              </Modal.RawClose>
            </Modal.FooterButtonGroup>
          </div>
        ) : (
          <FormikForm<{ updateOption: UpdateOption }>
            initialValues={{ updateOption: "replace_all" }}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, isSubmitting }) => (
              <>
                <Modal.Header>
                  <Modal.Title>Upload Bill</Modal.Title>
                  <Modal.Close />
                </Modal.Header>

                <Modal.Body>
                  <div className="t-text-subtitle-sm t-mb-6">
                    How would you like to handle the extracted bill information?
                  </div>

                  <RadioGroup.Root
                    value={values.updateOption}
                    onValueChange={(value) =>
                      setFieldValue("updateOption", value as UpdateOption)
                    }
                    className="t-flex t-flex-col t-gap-4"
                  >
                    <RadioGroup.Item
                      inline={false}
                      value="replace_all"
                      align="start"
                      id="replace_all"
                    >
                      <div className="t-flex t-flex-col t-gap-1">
                        <span className="t-text-body-sm t-text-text-100">
                          Extract & fill details
                        </span>
                        <span className="t-text-body-sm t-text-text-30 t-italic">
                          We will read the uploaded bill and fill in the details
                          automatically. You can review and edit before saving.
                        </span>
                      </div>
                    </RadioGroup.Item>

                    <RadioGroup.Item
                      inline={false}
                      value="skip_replacing"
                      align="start"
                      id="skip_replacing"
                    >
                      <div className="t-flex t-flex-col t-gap-1">
                        <span className="t-text-body-sm t-text-text-100">
                          Upload file only
                        </span>
                        <span className="t-text-body-sm t-text-text-30 t-italic">
                          Skip extraction and upload the bill attachment only.
                        </span>
                      </div>
                    </RadioGroup.Item>
                  </RadioGroup.Root>
                </Modal.Body>

                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button disabled={isSubmitting || isLoading}>Back</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    type="submit"
                    isLoading={isSubmitting || isLoading}
                    disabled={isSubmitting || isLoading}
                  >
                    Proceed
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            )}
          </FormikForm>
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
